import { createContext, useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { empresasService } from "app/services";
import { ErrorMessage, Spinner } from "app/components";

const ImportacionesContext = createContext();

export function ImportacionesProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const importacionesQuery = useQuery({
    queryKey: ["empresa:get:importaciones", params.empresa_id],
    queryFn: () =>
      empresasService.getImportaciones({ empresaId: params.empresa_id }),
  });

  const importacionAddMutation = useMutation({
    queryKey: ["empresa:add:importacion", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addImportacion(data), {
        loading: "Agregando la importación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await importacionesQuery.refetch();
    },
  });

  const importacionUpdateMutation = useMutation({
    queryKey: ["empresa:update:importacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateImportacion(data), {
        loading: "Actualizando la importación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await importacionesQuery.refetch();
    },
  });

  const importacionDeleteMutation = useMutation({
    queryKey: ["empresa:delete:importacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteImportacion(data), {
        loading: "Eliminando la importación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await importacionesQuery.refetch();
    },
  });

  const importacionVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:importacion"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyImportacion(data), {
        loading: "Verificando la importación...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await importacionesQuery.refetch();
    },
  });

  async function addImportacion({ importacion }) {
    return await importacionAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      importacion,
    });
  }

  async function updateImportacion({ importacionId, importacion }) {
    const { importacion_verificada, ...rest } = importacion;

    return await importacionUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      importacionId,
      importacion: rest,
    });
  }

  async function deleteImportacion({ importacionId }) {
    const deleted = window.confirm("¿Desea eliminar esta importación?");

    if (!!deleted) {
      await importacionDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        importacionId: importacionId,
      });

      return true;
    }

    return false;
  }

  async function verifyImportacion({ importacionId }) {
    const confirmed = window.confirm("¿Desea verificar esta importación?");

    if (confirmed) {
      return await importacionVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        importacionId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!importacionesQuery.isLoading) {
    return <Spinner label="Cargando las importaciones..." />;
  } else if (!!importacionesQuery.isError) {
    return (
      <ErrorMessage message={importacionesQuery.error.response.statusText} />
    );
  }

  return (
    <ImportacionesContext.Provider
      value={{
        importaciones: importacionesQuery.data,
        isLoading: importacionesQuery.isLoading,
        isFetching: importacionesQuery.isFetching,
        isRefetching: importacionesQuery.isRefetching,
        addImportacion,
        isAdding: importacionAddMutation.isLoading,
        updateImportacion,
        isUpdating: importacionUpdateMutation.isLoading,
        deleteImportacion,
        isDeleting: importacionDeleteMutation.isLoading,
        verifyImportacion,
        isVerifing: importacionVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </ImportacionesContext.Provider>
  );
}

export function useImportacionesContext() {
  const importacionesContext = useContext(ImportacionesContext);

  if (!importacionesContext) {
    throw new Error(
      "useImportacionesContext should be used inside the ImportacionesProvider."
    );
  }

  return importacionesContext;
}
